import emit from '../../../common/events';
import store, { waitForUser } from '../../../store/store';
import variables from '../../../common/styles/variables';
import { BaseComponent } from '../../base-component/base-component';
import { PaymentOrderDetails, PaymentOrderItemData } from '../order-completion.interface';
import { StoreState } from '../../../store/store.interface';
import { connect } from 'pwa-helpers';
import { css, html, nothing } from 'lit';
import {
  getClinicalReasoningAppUrl,
  getFa4AppUrl,
  getProfessionalPlpLink,
} from '../../../common/constants/professional-redirects';
import { getImageSrcUrl } from '../../../common/utils/product-util';
import { getOrder } from '../store/order-completion.selectors';
import { OfferTypeChecker } from '../../../common/utils/offer-type-util';
import { mapProductToFreeTrialInfo } from '../../free-trial/mappings/free-trial-info.mapping';
import { property, customElement, state } from 'lit/decorators.js';
import { selectUser } from '../../user-profile/user-profile.selectors';
import { SSOToWindow } from '../../sso/browse-context/direction/to-window/sso-to-window.service';
import { SSOUtils } from '../../sso/utils/sso.utils';
import {
  BOARDS_AND_BEYONDS_LAUNCH_CTA_LABEL,
  getBoardsBeyondLaunchUrl
} from '../../firstaid-forward/utils/boards-beyond.util';
import { CR_LAUNCH_CTA_LABEL } from '../../firstaid-forward/utils/clinical-reasoning.util';
import { formattedAuthors } from '../../../common/utils/util';
import { ariaLabelWithNewTab } from '../../../common/utils/a11y-util';


type CTASize = "small"|"large";

@customElement('mhecomm-order-confirmation-professional')
export class OrderConfirmationProfessional extends connect(store)(BaseComponent) {
  static styles = [
    variables,
    css`
      .product-image img {
        width: 100%;
      }
      .ft-agreements {
        font-size: var(--mh-font-size-extra-small);
      }
      @media (min-width: 992px) {
        .ft-agreements {
          max-width: 444px;
        }
      }
    `,
  ];

  @property()
  public orderNumber: string = '';

  @state()
  public firstName: string = '';

  @property({ type: Array })
  public products: PaymentOrderItemData[] = [];

  @state()
  orderDetails: PaymentOrderDetails | undefined;

  constructor() {
    super();
    new SSOToWindow(new SSOUtils()).exchangeTokenWhenReceiveReadySignal('boardsbeyond', '/dashboard');
  }

  connectedCallback(): void {
    super.connectedCallback();

    waitForUser().then(() => {
      const user = selectUser(store.getState() as StoreState);
      this.firstName = user?.givenName;
    });

    this.orderDetails = getOrder(store.getState() as StoreState);
  }

  renderComp() {
    return html`
      <div class="container">
        <section id="main" role="main">
          <h1 class="my-4">Thank you for your
              order${(this.firstName && this.firstName.length > 0) ? ', ' + this.firstName : nothing}!</h1>
          <p class="mb-0">
            Your order has been processed. Your order number is:
            <b data-automation-id="order-number">${this.orderNumber}</b>.
          </p>
          <p>You will receive an email summarizing your purchase shortly. If you do not receive an email within 5 minutes, please check your junk and spam folders.</p>
          ${this.products.map((product) => {
            return html`
              <div class="row mt-4">
                <div class="col-4 col-sm-3 col-md-2 col-lg-2">
                  <a href=${getProfessionalPlpLink(product.programSourceId)}>
                    <img class="img-fluid" src="${getImageSrcUrl(product?.isbn10.toString())}" alt=""/>
                  </a>
                </div>
                <div class="col-8 col-sm-9 col-md-10 col-lg-9 col-xl-8">
                  <a href=${getProfessionalPlpLink(product.programSourceId)}>
                    <h2 class="h5" data-automation-id=${`title-${product.isbn10}`}>${product.title}${product.edition ? `, ${product.edition}` : ''}</h2>
                  </a>
                  ${product.authors ? html`
                    <div><span class="fw-bold">By:</span> ${formattedAuthors(product.authors!, ',', ', ', ', ')}</div>` : nothing}
                  <div>
                  <span class="fw-bold" data-automation-id=${`format-${product.isbn10}`}>Format: </span
                  ><span>${product.format}</span>
                  </div>
                  <div>
                    <mhecomm-subscription-length labelSize="large" .product="${product}"/>
                  </div>
                  <div>
                    <mhecomm-subscription-access-through
                      fallbackDate="${this.orderDetails?.orderedDate ?? ''}"
                      .product="${product}"
                    />
                  </div>
                  <div>
                    <mhecomm-free-trial-end-date addClass="mb-0" .freeTrial="${mapProductToFreeTrialInfo(product)}"/>
                  </div>
                  <div class="d-none d-lg-block mt-3">
                    ${this.getOfferTypeCTA(product, "large")}
                  </div>
                  ${product.isFreeTrial
                    ? html`
                      <div class="ft-agreements d-none d-lg-block mt-3">
                        ${this.freeTrialAgreements(product)}
                      </div>`
                    : ''}
                </div>
              </div>
              <div class="d-lg-none">
                ${this.getOfferTypeCTA(product, "small")}
              </div>
              ${product.isFreeTrial
                ? html`
                  <div class="ft-agreements col-12 col-md-6 d-lg-none mt-3">
                    ${this.freeTrialAgreements(product)}
                  </div>
                `
                : ''}
            `;
          })}
        </section>
      </div>`;
  }

  getOfferTypeCTA(product: PaymentOrderItemData, size: CTASize) {
    switch (true) {
      case OfferTypeChecker.isBoardsBeyond(product.offerType):
        return this.getBoardsBeyondCTA(product);
      case OfferTypeChecker.isFirstAidForward(product.offerType):
        return this.getFirstAidForwardCTA(product, size);
      case OfferTypeChecker.isClinicalReasoning(product.offerType):
        return this.getClinicalReasoningCTA(product, size);
      default:
        return nothing;
    }
  }

  getBoardsBeyondCTA(product: PaymentOrderItemData) {
    return html`
    <a
      part="launch-fa4-large"
      class="btn btn-primary"
      data-automation-id=${`launch-boards-beyond-${product.isbn10}`}
      aria-label="${ariaLabelWithNewTab('BoardsBeyond App')}"
      @click=${(event: Event) => this.openBoardsBeyond(event, product)}
      target="_blank"
      href=${getBoardsBeyondLaunchUrl(product.boardsBeyondUrl)}
    >
      ${BOARDS_AND_BEYONDS_LAUNCH_CTA_LABEL}
    </a>`
  }

  getClinicalReasoningCTA(product: PaymentOrderItemData, size: CTASize = "large") {
    const url = getClinicalReasoningAppUrl();
    const extraCssClasses = "small" === size ? "col-12 col-md-6":"";
    return html`
      <a
        part="launch-cr-${size}"
        class="btn btn-primary ${extraCssClasses}"
        data-automation-id=${`launch-cr-${product.isbn10}`}
        aria-label="${ariaLabelWithNewTab('Clinical Reasoning App')}"
        @click=${() => this.analyticsGoToCrApp(url, product)}
        target="_blank"
        href=${url}
      >
        ${CR_LAUNCH_CTA_LABEL}
      </a>`
  }

  openBoardsBeyond(event: Event, product: PaymentOrderItemData) {
    event?.preventDefault();

    const boardsBeyondUrl = getBoardsBeyondLaunchUrl(product.boardsBeyondUrl);
    this.analyticsGoToBoardsBeyondApp(boardsBeyondUrl, product)
    window.open(boardsBeyondUrl);
  }

  getFirstAidForwardCTA(product: PaymentOrderItemData, size: CTASize = "large") {
    const fa4appUrl = getFa4AppUrl(product.programSourceId!);
    const extraCssClasses = "small" === size ? "col-12 col-md-6":"";
    return html`
    <a
      part="launch-fa4-${size}"
      class="btn btn-primary ${extraCssClasses}"
      data-automation-id=${`launch-first-aid-forward-${product.isbn10}`}
      aria-label="${ariaLabelWithNewTab('First Aid Forward App')}"
      @click=${() => this.analyticsGoToFA4App(fa4appUrl, product)}
      target="_blank"
      href=${fa4appUrl}
    >
      Launch First Aid Forward
    </a>`
  }

  /**
   * Shows agreements for free trial products.
   *
   * @param product
   */
  freeTrialAgreements(product: PaymentOrderItemData) {
    return html`
      <mhecomm-free-trial-agreements
        .freeTrial="${mapProductToFreeTrialInfo(product)}"
        showBaseAgreements=${false}/>
    `;
  }

  analyticsGoToFA4App(url: string, product: PaymentOrderItemData) {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'go-to-fa4-app-order-confirmation',
        label: 'Launch First Aid Forward',
        href: url,
        productOrdered: product.isFreeTrial ? 'freetrial': 'purchasable'
      }
    });
  }

  analyticsGoToCrApp(url: string, product: PaymentOrderItemData) {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'go-to-cr-app-order-confirmation',
        label: CR_LAUNCH_CTA_LABEL,
        href: url,
        productOrdered: product.isFreeTrial ? 'freetrial': 'purchasable'
      }
    });
  }

  analyticsGoToBoardsBeyondApp(url: string, product: PaymentOrderItemData) {
    emit({
      element: this,
      name: 'click',
      detail: {
        eventName: 'go-to-boards-beyond-app-order-confirmation',
        label: 'Launch BoardsBeyond',
        href: url,
        productOrdered: product.isFreeTrial ? 'freetrial': 'purchasable'
      }
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-order-confirmation-professional': OrderConfirmationProfessional;
  }
}
